import React, { useEffect, useState } from 'react'
import { OmSwitch, OmButton, OmCheckbox, OmSpinner, OmDropdownMenu } from '@owma/kit'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import plusIcon from 'assets/images/settings/setting-plus-24.svg'
import moreIcon from 'assets/images/more-vert-24.svg'

import styles from './style.module.scss'

import EmailInput from './EmailInput'

import mailboxApiHelper from 'services/mailbox-api'

interface InitData {
  email: string[]
  enabled: boolean
  options: string
}

interface ForwardingPanelProps {
  active: boolean
}

const ForwardingPanel: React.FC<ForwardingPanelProps> = ({ active }) => {
  const { t } = useTranslation()

  const [isEnabled, setIsEnabled] = useState(false)
  const [keepCopy, setKeepCopy] = useState(true)
  const [forwardingEmail, setForwardingEmail] = useState<string[]>([])
  const [infoId, setInfoId] = useState<number | null>(null)

  const [loading, setLoading] = useState(false)
  const [isChanged, setIsChanged] = useState(false)

  const [isEmailInputOpen, setIsEmailInputOpen] = useState(false)
  const [emailInputData, setEmailInputData] = useState<string | null>(null)
  const [initData, setInitData] = useState<InitData>({
    email: [],
    enabled: false,
    options: 'keep',
  })

  useEffect(() => {
    const loadForwarding = async (): Promise<void> => {
      try {
        const res = await mailboxApiHelper.getForward(null)
        console.log(' - res : ', res)
        const data = res.data
        if (Object.keys(data).length === 0) {
          setInitData({
            email: [],
            enabled: false,
            options: 'keep',
          })
        } else {
          initData.email = data.email.split(', ')
          initData.enabled = data.enabled
          initData.options = data.options
        }

        setForwardingEmail(initData.email)
        setIsEnabled(initData.enabled)
        setKeepCopy(initData.options === 'keep')
        setInfoId(data.id || null)
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }

    if (active) {
      setLoading(true)
      loadForwarding()
    } else {
      setLoading(true)

      setIsEnabled(false)
      setForwardingEmail([])
      setKeepCopy(true)
      setInfoId(null)
      setInitData({
        email: [],
        enabled: false,
        options: 'keep',
      })
    }
  }, [active])

  useEffect(() => {
    const changed =
      forwardingEmail.length !== initData.email?.length ||
      forwardingEmail.some((email, index) => email !== initData.email?.[index]) ||
      isEnabled !== initData.enabled ||
      keepCopy !== (initData.options === 'keep')
    setIsChanged(changed)
  }, [forwardingEmail, isEnabled, keepCopy, initData])

  const handleEnableChange = (checked: boolean): void => {
    setIsEnabled(checked)
  }

  const handleKeepCopyChange = (checked: boolean): void => {
    setKeepCopy(checked)
  }

  const handleAdd = (): void => {
    setIsEmailInputOpen(true)
    setEmailInputData(null)
  }

  const handleEdit = (index: number): void => {
    setIsEmailInputOpen(true)
    setEmailInputData(forwardingEmail[index])
  }

  const handleDelete = (index: number): void => {
    setForwardingEmail(forwardingEmail.filter((_, i) => i !== index))
  }

  const handleSave = async (): Promise<void> => {
    setLoading(true)
    try {
      let res = null
      if (forwardingEmail.length === 0) {
        if (infoId) {
          res = await mailboxApiHelper.deleteForward({})
        }
      } else if (infoId) {
        res = await mailboxApiHelper.putForward({
          email: forwardingEmail.join(', '),
          enabled: isEnabled,
          options: keepCopy ? 'keep' : 'delete',
        })
      } else {
        res = await mailboxApiHelper.postForward({
          email: forwardingEmail.join(', '),
          enabled: isEnabled,
          options: keepCopy ? 'keep' : 'delete',
        })
      }
      setInitData({
        email: forwardingEmail,
        enabled: isEnabled,
        options: keepCopy ? 'keep' : 'delete',
      })
      setInfoId(res?.data?.id || null)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.forwardingPanel}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('settings.accountTabs.forwarding')}</h2>
      </div>

      <OmSpinner size="large" spinning={loading}>
        <div className={styles.content}>
          <div className={styles.enableRow}>
            <OmSwitch checked={isEnabled} onChange={handleEnableChange} className={styles.switch} />
            <span className={styles.enableText}>{t('settings.accountTabs.forwardingEnable')}</span>
            <OmButton
              variant="primary"
              icon={<img src={plusIcon} alt="plus" />}
              onClick={handleAdd}
              className={styles.addButton}
            />
          </div>

          {isEnabled ? (
            <>
              <div className={classNames(styles.forwardingRow, { [styles.disabled]: !isEnabled })}>
                {forwardingEmail.map((email, index) => (
                  <div
                    key={index}
                    className={styles.forwardingEmail}
                    onMouseEnter={(e) => e.stopPropagation()}
                  >
                    <div className={styles.emailText}>{email}</div>
                    <OmDropdownMenu
                      menu={{
                        items: [
                          {
                            key: 'edit',
                            label: t('common.button.edit'),
                            onClick: () => handleEdit(index),
                          },
                          {
                            key: 'delete',
                            label: t('common.button.delete'),
                            onClick: () => handleDelete(index),
                          },
                        ],
                      }}
                      trigger={['click']}
                      overlayClassName="contextMenu"
                    >
                      <button className={styles.moreButton}>
                        <img src={moreIcon} alt="more" />
                      </button>
                    </OmDropdownMenu>
                  </div>
                ))}
              </div>

              <div className={classNames(styles.keepCopyRow, { [styles.disabled]: !isEnabled })}>
                <OmCheckbox
                  checked={keepCopy}
                  onChange={handleKeepCopyChange}
                  disabled={!isEnabled}
                  className={styles.checkbox}
                />
                <span className={styles.keepCopyText}>
                  {t('settings.accountTabs.forwardingKeepCopy')}
                </span>
              </div>
            </>
          ) : (
            <div className={styles.disabledRow}>
              <span className={styles.disabledText}>
                {t('settings.accountTabs.forwardingDisabled')}
              </span>
            </div>
          )}
          {isChanged && (
            <div className={styles.saveButtonContainer}>
              <button className={styles.saveButton} onClick={handleSave}>
                {t('common.button.save')}
              </button>
            </div>
          )}
        </div>

        {isEmailInputOpen && (
          <EmailInput
            onClose={() => setIsEmailInputOpen(false)}
            onSave={({ email }) => {
              setIsEmailInputOpen(false)
              if (forwardingEmail.includes(email)) {
                alert(t('settings.accountTabs.forwardingEmailAlreadyExists'))
                return
              }
              setForwardingEmail([...forwardingEmail, email])
            }}
            initialEmail={emailInputData || undefined}
          />
        )}
      </OmSpinner>
    </div>
  )
}

export default ForwardingPanel
